import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, FloppyDisk, UploadSimple, Plus, X, ArrowSquareOut, User } from "@phosphor-icons/react";
import MarkdownField from "@/components/MarkdownField";

const empty = {
  title: "", subtitle: "", slug: "", short_description: "", full_description: "",
  category: "", tags: [], keywords: [],
  state: "", city: "", country: "",
  latitude: null, longitude: null,
  featured_image: "", gallery: [], pdfs: [], video_url: "",
  faq: [], facts: [],
  author_name: "", author_role: "",
  sections: [], layout_mode: "full", theme: "classic",
  status: "draft",
};

const THEMES = [
  { id: "classic", label: "Classic", swatch: ["#FFFFFF", "#C2410C"] },
  { id: "saffron", label: "Saffron Heritage", swatch: ["#FFF7ED", "#EA580C"] },
  { id: "royal-blue", label: "Royal Blue", swatch: ["#EFF6FF", "#1E40AF"] },
  { id: "forest", label: "Forest", swatch: ["#ECFDF5", "#166534"] },
  { id: "sepia", label: "Sepia (Archival)", swatch: ["#F5EDE0", "#92400E"] },
  { id: "midnight", label: "Midnight", swatch: ["#0B1120", "#FBBF24"] },
];

const MD_HINT = "Markdown supported: **bold**, *italic*, - bullet, 1. numbered, # heading, [link](url), > quote";

export default function PageEditor({ mode }) {
  const { id } = useParams();
  const nav = useNavigate();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(mode === "edit");
  const [saving, setSaving] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [factInput, setFactInput] = useState("");

  useEffect(() => {
    if (mode === "edit" && id) {
      api.get(`/pages/${id}`)
        .then(({ data }) => setForm({ ...empty, ...data }))
        .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
        .finally(() => setLoading(false));
    }
  }, [mode, id]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    // Store the RELATIVE URL (e.g. "/api/files/xxx.jpg") so it works across preview/production.
    // Browser resolves it relative to the current origin, which routes /api/* to the backend.
    return data.url;
  };

  const handleFeaturedUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const url = await upload(f);
      setField("featured_image", url);
      toast.success("Featured image uploaded");
    } catch (err) { toast.error("Upload failed"); }
  };

  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    try {
      const urls = await Promise.all(files.map(upload));
      setField("gallery", [...(form.gallery || []), ...urls]);
      toast.success(`${urls.length} image(s) added`);
    } catch (err) { toast.error("Upload failed"); }
  };

  const handlePdfUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    try {
      const items = await Promise.all(files.map(async (f) => ({ name: f.name, url: await upload(f) })));
      setField("pdfs", [...(form.pdfs || []), ...items]);
      toast.success(`${items.length} document(s) added`);
    } catch (err) { toast.error("Upload failed"); }
  };

  const save = async (publish = null) => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (publish !== null) payload.status = publish ? "published" : "draft";
      // Sanitize: empty strings for slug => null
      if (!payload.slug) delete payload.slug;
      const res = mode === "new"
        ? await api.post("/pages", payload)
        : await api.put(`/pages/${id}`, payload);
      toast.success("Page saved");
      if (mode === "new") nav(`/admin/pages/${res.data.id}`);
      else setForm({ ...empty, ...res.data });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (loading) return <div className="skel" style={{ height: 400 }} />;

  return (
    <div data-testid="page-editor">
      <Link to="/admin/pages" className="btn btn-ghost" data-testid="editor-back" style={{ marginBottom: 16 }}>
        <ArrowLeft size={14} /> All pages
      </Link>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 24, gap: 16, flexWrap: "wrap" }}>
        <div>
          <div className="overline">{mode === "new" ? "Create page" : "Edit page"}</div>
          <h1 className="font-display" style={{ fontSize: 36, fontWeight: 900, margin: "8px 0 0", letterSpacing: "-0.03em" }}>
            {form.title || "Untitled page"}
          </h1>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {form.slug && form.status === "published" && (
            <a className="btn" href={`/p/${form.slug}`} target="_blank" rel="noreferrer" data-testid="view-public-link">
              <ArrowSquareOut size={14} /> View
            </a>
          )}
          <button className="btn" onClick={() => save(false)} disabled={saving} data-testid="editor-save-draft">
            <FloppyDisk size={14} /> Save Draft
          </button>
          <button className="btn btn-primary" onClick={() => save(true)} disabled={saving} data-testid="editor-publish">
            Publish
          </button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
        {/* LEFT */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div className="card card-pad">
            <div className="overline" style={{ marginBottom: 16 }}>Basic information</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label className="label">Title</label>
                <input className="input" value={form.title} onChange={(e) => setField("title", e.target.value)} data-testid="editor-title" />
              </div>
              <div>
                <label className="label">Subtitle</label>
                <input className="input" value={form.subtitle} onChange={(e) => setField("subtitle", e.target.value)} data-testid="editor-subtitle" />
              </div>
              <div>
                <label className="label">Slug (auto if empty)</label>
                <input className="input" value={form.slug || ""} onChange={(e) => setField("slug", e.target.value)} data-testid="editor-slug" />
              </div>
              <div>
                <label className="label">Short description</label>
                <MarkdownField value={form.short_description}
                  onChange={(v) => setField("short_description", v)}
                  minHeight={90} testid="editor-short-desc" />
              </div>
              <div>
                <label className="label">Full description</label>
                <MarkdownField value={form.full_description}
                  onChange={(v) => setField("full_description", v)}
                  minHeight={220} testid="editor-full-desc" />
              </div>
            </div>
          </div>

          <div className="card card-pad">
            <div className="overline" style={{ marginBottom: 16 }}>Media</div>
            <div>
              <label className="label">Featured image</label>
              {form.featured_image ? (
                <div style={{ position: "relative", marginBottom: 12, background: "var(--surface)", border: "1px solid var(--border)" }}>
                  <img src={form.featured_image} alt="featured"
                    style={{ display: "block", width: "100%", height: "auto", maxHeight: 420, objectFit: "contain" }} />
                  <button className="btn btn-sm" onClick={() => setField("featured_image", "")} data-testid="editor-clear-featured"
                    style={{ position: "absolute", top: 8, right: 8 }}><X size={12} /></button>
                </div>
              ) : (
                <label className="btn" style={{ cursor: "pointer", width: "100%", justifyContent: "center" }}>
                  <UploadSimple size={16} /> Upload image
                  <input type="file" accept="image/*" onChange={handleFeaturedUpload} hidden data-testid="editor-featured-upload" />
                </label>
              )}
            </div>

            <div style={{ marginTop: 16 }}>
              <label className="label">Gallery</label>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(120px,1fr))", gap: 8, marginBottom: 8 }}>
                {(form.gallery || []).map((url, i) => (
                  <div key={i} style={{ position: "relative" }}>
                    <img src={url} alt="" style={{ width: "100%", height: 90, objectFit: "cover", border: "1px solid var(--border)" }} />
                    <button className="btn btn-sm" onClick={() => setField("gallery", form.gallery.filter((_, x) => x !== i))}
                      style={{ position: "absolute", top: 4, right: 4, padding: "2px 6px" }}><X size={10} /></button>
                  </div>
                ))}
              </div>
              <label className="btn" style={{ cursor: "pointer" }}>
                <UploadSimple size={14} /> Add images
                <input type="file" accept="image/*" multiple onChange={handleGalleryUpload} hidden data-testid="editor-gallery-upload" />
              </label>
            </div>

            <div style={{ marginTop: 16 }}>
              <label className="label">PDF Attachments</label>
              {(form.pdfs || []).map((p, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: 8, border: "1px solid var(--border)", marginBottom: 6, fontSize: 13 }}>
                  <a href={p.url} target="_blank" rel="noreferrer" style={{ color: "var(--accent)" }}>{p.name}</a>
                  <button className="btn btn-sm" onClick={() => setField("pdfs", form.pdfs.filter((_, x) => x !== i))}><X size={10} /></button>
                </div>
              ))}
              <label className="btn" style={{ cursor: "pointer" }}>
                <UploadSimple size={14} /> Add PDF
                <input type="file" accept=".pdf,application/pdf" multiple onChange={handlePdfUpload} hidden data-testid="editor-pdf-upload" />
              </label>
            </div>

            <div style={{ marginTop: 16 }}>
              <label className="label">Video URL (YouTube / Vimeo)</label>
              <input className="input" value={form.video_url} onChange={(e) => setField("video_url", e.target.value)} placeholder="https://youtube.com/watch?v=..." data-testid="editor-video-url" />
            </div>
          </div>

          <div className="card card-pad">
            <div className="overline" style={{ marginBottom: 16 }}>FAQ</div>
            {(form.faq || []).map((q, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, marginBottom: 10, padding: 10, border: "1px solid var(--border)" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <input className="input" placeholder="Question" value={q.q}
                    onChange={(e) => setField("faq", form.faq.map((x, j) => j === i ? { ...x, q: e.target.value } : x))} />
                  <textarea className="textarea" placeholder="Answer" style={{ minHeight: 60 }} value={q.a}
                    onChange={(e) => setField("faq", form.faq.map((x, j) => j === i ? { ...x, a: e.target.value } : x))} />
                </div>
                <button className="btn btn-sm" onClick={() => setField("faq", form.faq.filter((_, j) => j !== i))}><X size={12} /></button>
              </div>
            ))}
            <button className="btn" onClick={() => setField("faq", [...(form.faq || []), { q: "", a: "" }])} data-testid="editor-add-faq">
              <Plus size={12} /> Add FAQ
            </button>
          </div>

          {/* Custom Tabs / Sections */}
          <div className="card card-pad" data-testid="editor-sections-card">
            <div className="overline" style={{ marginBottom: 12 }}>Custom Tabs / Sections</div>
            <p style={{ fontSize: 12, color: "var(--text-2)", marginTop: 0 }}>
              Sections appear as tabs at the top of the public page (after an automatic <b>Overview</b> tab).
              Use Markdown in content (use <code>- item</code> for bullets, <code>**bold**</code>, <code># Heading</code>).
            </p>
            {(form.sections || []).map((s, i) => (
              <div key={i} style={{ border: "1px solid var(--border)", padding: 12, marginBottom: 10 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
                  <input className="input" placeholder="Section name (e.g. Photo Gallery)" value={s.name || ""}
                    data-testid={`section-name-${i}`}
                    onChange={(e) => setField("sections", form.sections.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} />
                  <button className="btn btn-sm" onClick={() => setField("sections", form.sections.filter((_, j) => j !== i))} data-testid={`section-remove-${i}`}><X size={12} /></button>
                </div>
                <MarkdownField
                  value={s.content || ""}
                  onChange={(v) => setField("sections", form.sections.map((x, j) => j === i ? { ...x, content: v } : x))}
                  minHeight={140}
                  placeholder="Section content — use the toolbar above to format"
                  testid={`section-content-${i}`}
                />
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 10 }}>
                  <label className="btn btn-sm" style={{ cursor: "pointer" }}>
                    <UploadSimple size={12} /> Add images ({(s.gallery || []).length})
                    <input type="file" multiple accept="image/*" hidden
                      onChange={async (e) => {
                        const files = Array.from(e.target.files || []);
                        const urls = await Promise.all(files.map(upload));
                        setField("sections", form.sections.map((x, j) => j === i ? { ...x, gallery: [...(x.gallery || []), ...urls] } : x));
                      }} />
                  </label>
                  <label className="btn btn-sm" style={{ cursor: "pointer" }}>
                    <UploadSimple size={12} /> Add PDFs ({(s.pdfs || []).length})
                    <input type="file" multiple accept=".pdf" hidden
                      onChange={async (e) => {
                        const files = Array.from(e.target.files || []);
                        const items = await Promise.all(files.map(async (f) => ({ name: f.name, url: await upload(f) })));
                        setField("sections", form.sections.map((x, j) => j === i ? { ...x, pdfs: [...(x.pdfs || []), ...items] } : x));
                      }} />
                  </label>
                </div>
                <input className="input" placeholder="Video URL (optional)" style={{ marginTop: 8 }}
                  value={s.video_url || ""}
                  onChange={(e) => setField("sections", form.sections.map((x, j) => j === i ? { ...x, video_url: e.target.value } : x))} />
                {(s.gallery?.length > 0 || s.pdfs?.length > 0) && (
                  <div style={{ display: "flex", gap: 4, marginTop: 8, flexWrap: "wrap" }}>
                    {(s.gallery || []).map((u, k) => (
                      <div key={k} style={{ position: "relative" }}>
                        <img alt="" src={u} style={{ width: 56, height: 56, objectFit: "cover", border: "1px solid var(--border)" }} />
                        <button className="btn btn-sm" style={{ position: "absolute", top: -6, right: -6, padding: 0, width: 16, height: 16 }}
                          onClick={() => setField("sections", form.sections.map((x, j) => j === i ? { ...x, gallery: x.gallery.filter((_, m) => m !== k) } : x))}><X size={8} /></button>
                      </div>
                    ))}
                    {(s.pdfs || []).map((p, k) => (
                      <div key={k} className="badge badge-muted" style={{ fontSize: 10 }}>
                        {p.name}
                        <button onClick={() => setField("sections", form.sections.map((x, j) => j === i ? { ...x, pdfs: x.pdfs.filter((_, m) => m !== k) } : x))}
                          style={{ marginLeft: 4, background: "transparent", border: 0, cursor: "pointer" }}><X size={8} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <button className="btn" onClick={() => setField("sections", [...(form.sections || []), { name: "", content: "", gallery: [], pdfs: [], video_url: "" }])} data-testid="editor-add-section">
              <Plus size={12} /> Add section
            </button>
          </div>
        </div>

        {/* RIGHT */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div className="card card-pad">
            <div className="overline" style={{ marginBottom: 12 }}>Status</div>
            <select className="select" value={form.status} onChange={(e) => setField("status", e.target.value)} data-testid="editor-status">
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </div>

          <div className="card card-pad" data-testid="author-card">
            <div className="overline" style={{ marginBottom: 12 }}>
              <User size={12} weight="bold" style={{ verticalAlign: "middle", marginRight: 4 }} />
              Author / Created by
            </div>
            <p style={{ fontSize: 12, color: "var(--text-2)", margin: "0 0 10px" }}>
              Credited at the bottom of the public page.
            </p>
            <label className="label">Name</label>
            <input className="input" value={form.author_name || ""}
              onChange={(e) => setField("author_name", e.target.value)}
              placeholder="e.g. Aditi Sharma"
              data-testid="editor-author-name" />
            <label className="label" style={{ marginTop: 10 }}>Role (optional)</label>
            <input className="input" value={form.author_role || ""}
              onChange={(e) => setField("author_role", e.target.value)}
              placeholder="e.g. Historian, Photographer, Guide"
              data-testid="editor-author-role" />
          </div>

          <div className="card card-pad" data-testid="theme-picker">
            <div className="overline" style={{ marginBottom: 12 }}>Page theme</div>
            <p style={{ fontSize: 12, color: "var(--text-2)", margin: "0 0 12px" }}>
              Pick a color theme applied to the public scan page.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {THEMES.map((t) => (
                <button key={t.id} type="button" onClick={() => setField("theme", t.id)}
                  data-testid={`theme-${t.id}`}
                  style={{
                    display: "flex", flexDirection: "column", gap: 6, padding: 10,
                    border: "2px solid " + (form.theme === t.id ? "#000" : "var(--border)"),
                    background: form.theme === t.id ? "var(--surface)" : "#fff",
                    cursor: "pointer", textAlign: "left", fontFamily: "inherit"
                  }}>
                  <div style={{ display: "flex", height: 24, border: "1px solid var(--border)" }}>
                    <div style={{ flex: 1, background: t.swatch[0] }} />
                    <div style={{ flex: 1, background: t.swatch[1] }} />
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 600 }}>{t.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="card card-pad">
            <div className="overline" style={{ marginBottom: 12 }}>Categorization</div>
            <label className="label">Category</label>
            <input className="input" value={form.category} onChange={(e) => setField("category", e.target.value)} data-testid="editor-category" />
            <label className="label" style={{ marginTop: 12 }}>Tags</label>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
              {(form.tags || []).map((t, i) => (
                <span key={i} className="badge badge-muted">
                  {t}
                  <button onClick={() => setField("tags", form.tags.filter((_, j) => j !== i))} style={{ background: "transparent", border: 0, cursor: "pointer", marginLeft: 4 }}><X size={10} /></button>
                </span>
              ))}
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <input className="input" value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (tagInput.trim()) { setField("tags", [...(form.tags || []), tagInput.trim()]); setTagInput(""); } } }}
                placeholder="Press Enter to add" data-testid="editor-tag-input" />
            </div>
          </div>

          <div className="card card-pad">
            <div className="overline" style={{ marginBottom: 12 }}>Location</div>
            <label className="label">City</label>
            <input className="input" value={form.city} onChange={(e) => setField("city", e.target.value)} data-testid="editor-city" />
            <label className="label" style={{ marginTop: 8 }}>State</label>
            <input className="input" value={form.state} onChange={(e) => setField("state", e.target.value)} />
            <label className="label" style={{ marginTop: 8 }}>Country</label>
            <input className="input" value={form.country} onChange={(e) => setField("country", e.target.value)} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 }}>
              <div>
                <label className="label">Latitude</label>
                <input className="input" type="number" step="any" value={form.latitude ?? ""}
                  onChange={(e) => setField("latitude", e.target.value === "" ? null : parseFloat(e.target.value))} />
              </div>
              <div>
                <label className="label">Longitude</label>
                <input className="input" type="number" step="any" value={form.longitude ?? ""}
                  onChange={(e) => setField("longitude", e.target.value === "" ? null : parseFloat(e.target.value))} />
              </div>
            </div>
          </div>

          <div className="card card-pad">
            <div className="overline" style={{ marginBottom: 12 }}>Quick facts</div>
            {(form.facts || []).map((f, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: 13 }}>
                <span>• {f}</span>
                <button className="btn btn-sm" onClick={() => setField("facts", form.facts.filter((_, j) => j !== i))}><X size={10} /></button>
              </div>
            ))}
            <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
              <input className="input" value={factInput} onChange={(e) => setFactInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (factInput.trim()) { setField("facts", [...(form.facts || []), factInput.trim()]); setFactInput(""); } } }}
                placeholder="Press Enter to add" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
